import type { UserGamePack } from "@skillscratch/shared-types";
import { useFirebaseUser } from "./useFirebaseUser";
import { collection, getDocs, onSnapshot, query, where, doc } from "firebase/firestore";
import { firestore } from "@/helpers/firebase";
import { useState, useEffect } from "react";

export function useSingleGamePack(gamePackId: string): [UserGamePack | undefined, boolean, Error | undefined] {
  const user = useFirebaseUser();
  const [gamePack, setGamePack] = useState<UserGamePack | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  
  useEffect(() => {
    const userId = user?.uid;
    if (!userId || !gamePackId) {
      setGamePack(undefined);
      setLoading(false);
      return;
    }
    
    // Reference to the specific game pack document
    const gamePackDocRef = doc(firestore, "user", userId, "gamePack", gamePackId);
    
    // Set up onSnapshot listener for the specific document
    const unsubscribe = onSnapshot(
      gamePackDocRef,
      async (docSnapshot) => {
        try {
          if (!docSnapshot.exists()) {
            setGamePack(undefined);
            setLoading(false);
            return;
          }
          
          const pack = docSnapshot.data() as UserGamePack;
          
          // Get total session count
          const totalSessionSnapshot = await getDocs(
            query(collection(docSnapshot.ref, "session"))
          );
          const totalSessionCount = totalSessionSnapshot.size;
          
          // Get count of incomplete sessions
          const incompleteSessionSnapshot = await getDocs(
            query(collection(docSnapshot.ref, "session"), where("completed", "==", false))
          );
          const incompleteSessionCount = incompleteSessionSnapshot.size;
          
          // Calculate used sessions (total - incomplete)
          const usedSessionCount = totalSessionCount - incompleteSessionCount;
          
          // Available count is the pack's total count minus used sessions
          const count = pack.count - usedSessionCount;
          
          const processedPack = {
            id: pack.id,
            gameThemeId: pack.gameThemeId,
            count,
            storeId: pack.storeId,
            usedCount: usedSessionCount,
          } satisfies UserGamePack;
          
          // Only set the gamepack if it has available count
          if (processedPack.count > 0) {
            setGamePack(processedPack);
          } else {
            setGamePack(undefined);
          }
          
          setLoading(false);
          setError(undefined);
        } catch (err) {
          setGamePack(undefined);
          setLoading(false);
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      },
      (err) => {
        setGamePack(undefined);
        setLoading(false);
        setError(err);
      }
    );
    
    // Clean up listener on unmount
    return () => unsubscribe();
  }, [user, gamePackId]);
  
  return [gamePack, loading, error];
}